import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import { usePageTitle } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import { callObservationsAPI, observabilityAPI, metricAnnotationsAPI, scenariosAPI } from "../../services/api";
import { useCan } from "../../hooks/useCan";
import AnnotateMetricModal from "../../components/metrics/AnnotateMetricModal";
import useMetricPolling from "../../hooks/useMetricPolling";
import AudioWaveformPlayer from "../../components/AudioWaveformPlayer";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, RotateCw, Bug, Play } from "lucide-react";
import DebugDrawer from "../../components/DebugDrawer";
import {
  StandardMetricsPanel,
  SystemLlmMetricsSection,
  CustomLlmMetricsSection,
  CallInsightsPanel,
  TranscriptPanel,
} from "../../components/CallAnalysis";

// ─── Main page ─────────────────────────────────────────────────────────────────

const ConversationDetail = () => {
  const { id } = useParams();
  const { setPageTitle } = usePageTitle();
  const { currentWorkspace, user } = useAuth();
  const navigate = useWsNavigate();
  const canCreateReplayScenario = useCan("scenario", "write");
  const canRerunMetrics = useCan("rerun_metrics_on_run", "write");

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekTo, setSeekTo] = useState(null);
  const [adjacentIds, setAdjacentIds] = useState({ prev: null, next: null });
  const [annotateModal, setAnnotateModal] = useState(null);
  const [rerunDropdownOpen, setRerunDropdownOpen] = useState(false);
  const [rerunMode, setRerunMode] = useState("menu"); // "menu" | "select"
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const [rerunning, setRerunning] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [replayLoading, setReplayLoading] = useState(false);

  const isCallFailed = (data) =>
    data?.analysis_status === "failed" || data?.analysis_status === "error";

  const isRecordingPending = (data) =>
    !isCallFailed(data) &&
    (!data?.call_recording_url ||
      data.call_recording_url.startsWith("pending://"));

  const hasPendingMetrics = useCallback((data) =>
    Array.isArray(data?.metric_results_data) &&
    data.metric_results_data.some((m) => m.status === "pending"), []);

  const isStillPending = useCallback((data) =>
    data.analysis_status === "pending" ||
    data.analysis_status === "processing" ||
    isRecordingPending(data) ||
    hasPendingMetrics(data), [hasPendingMetrics]);

  const fetchCallData = useCallback(async () => {
    const res = await callObservationsAPI.getCallObservation(currentWorkspace.id, id);
    return res?.data || null;
  }, [currentWorkspace, id]);

  const { updateData, startPolling, stopPolling, setMetricsPending } = useMetricPolling(
    fetchCallData,
    setCall,
    {
      recordingUrlKey: "call_recording_url",
      isPending: isStillPending,
    }
  );

  useEffect(() => {
    setPageTitle("Call Analysis");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  useEffect(() => {
    if (currentWorkspace) loadCall();
    return () => stopPolling();
    // eslint-disable-next-line
  }, [id, currentWorkspace]);

  const loadCall = async () => {
    setLoading(true);
    try {
      const data = await fetchCallData();
      if (data) {
        setCall(data);
        startPolling(data);
      }

      try {
        const listRes = await callObservationsAPI.getCallObservations(
          currentWorkspace.id,
          { per_page: 200 },
        );
        const allIds = (listRes.data?.data || listRes.data || []).map((c) => c.id);
        const idx = allIds.indexOf(Number(id));
        setAdjacentIds({
          prev: idx > 0 ? allIds[idx - 1] : null,
          next: idx < allIds.length - 1 ? allIds[idx + 1] : null,
        });
      } catch {
        setAdjacentIds({ prev: null, next: null });
      }
    } catch (e) {
      console.error("Failed to load call", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
        Loading call analysis…
      </div>
    );
  if (!call)
    return (
      <div className="flex items-center justify-center py-20 text-sm text-red-500">
        Call not found.
      </div>
    );

  const analysisFailed = call.analysis_status === "failed";

  // Flat, de-duped list of metrics that can be individually re-run, built from
  // the call's evaluated metrics (standard audio + system LLM + custom).
  const rerunableMetrics = (() => {
    const groups = [call.standard_metrics, call.system_llm_metrics, call.llm_metrics];
    const seen = new Set();
    const out = [];
    groups.forEach((g) =>
      (g || []).forEach((m) => {
        if (m.metric_id == null || seen.has(m.metric_id)) return;
        seen.add(m.metric_id);
        out.push({ id: m.metric_id, name: m.metric_name });
      })
    );
    return out;
  })();

  // Text conversations have no audio. The detail endpoint exposes the explicit
  // modality field `observation_type` ("voice" | "text"). Prefer it; fall back
  // to "no recording URL + not failed + transcript present" if it's missing.
  const hasTranscript =
    (Array.isArray(call.generated_transcript)
      ? call.generated_transcript.length > 0
      : !!call.generated_transcript) ||
    (Array.isArray(call.call_transcript)
      ? call.call_transcript.length > 0
      : !!call.call_transcript);
  const isTextConversation =
    call.observation_type === "text" ||
    (call.observation_type == null &&
      !call.call_recording_url &&
      !isCallFailed(call) &&
      hasTranscript);

  const channelLabel =
    call.ai_channel != null
      ? {
          ai_channel: call.ai_channel,
          user_channel: 1 - call.ai_channel,
          confidence: call.channel_confidence,
        }
      : null;

  const handleTimestampClick = (ms) => {
    setSeekTo({ seconds: ms / 1000, id: Date.now() });
  };

  const handleRerun = async (type) => {
    closeRerunDropdown();
    setRerunning(true);
    try {
      // Set metrics to pending state locally immediately
      if (type === "all" || type === "standard") {
        setMetricsPending(["standard_metrics", "system_llm_metrics", "llm_metrics"]);
      } else if (type === "llm") {
        setMetricsPending(["system_llm_metrics", "llm_metrics"]);
      }

      const rerunFn =
        type === "all" ? observabilityAPI.rerunAllMetrics :
        type === "standard" ? observabilityAPI.rerunAudioMetrics :
        observabilityAPI.rerunLlmMetrics;
      await rerunFn(call.id);

      const data = await fetchCallData();
      if (data) {
        updateData(data);
        startPolling(data);
      }
    } catch (err) {
      console.error("Rerun failed:", err);
    } finally {
      setRerunning(false);
    }
  };

  const closeRerunDropdown = () => {
    setRerunDropdownOpen(false);
    setRerunMode("menu");
  };

  const toggleMetricSelection = (metricId) => {
    setSelectedMetricIds((prev) =>
      prev.includes(metricId) ? prev.filter((x) => x !== metricId) : [...prev, metricId]
    );
  };

  const handleRerunSelected = async () => {
    if (!selectedMetricIds.length) return;
    closeRerunDropdown();
    setRerunning(true);
    try {
      // Reflect the selected metrics as pending immediately.
      setMetricsPending(["standard_metrics", "system_llm_metrics", "llm_metrics"]);
      await observabilityAPI.rerunSelectedMetrics(call.id, selectedMetricIds);
      setSelectedMetricIds([]);

      const data = await fetchCallData();
      if (data) {
        updateData(data);
        startPolling(data);
      }
    } catch (err) {
      console.error("Selected rerun failed:", err);
    } finally {
      setRerunning(false);
    }
  };

  // Rerun a single metric (from the per-metric kebab menu) on this call.
  const handleRerunSingleMetric = async (metricId) => {
    if (!metricId) return;
    setRerunning(true);
    try {
      setMetricsPending(["standard_metrics", "system_llm_metrics", "llm_metrics"]);
      await observabilityAPI.rerunSelectedMetrics(call.id, [metricId]);
      const data = await fetchCallData();
      if (data) {
        updateData(data);
        startPolling(data);
      }
    } catch (err) {
      console.error("Single-metric rerun failed:", err);
    } finally {
      setRerunning(false);
    }
  };

  const handleCreateReplayScenario = async () => {
    if (!call?.id || !call?.agent?.id) return;
    setReplayLoading(true);
    try {
      const response = await scenariosAPI.generateFromReplay(
        call.id,
        call.agent.id,
        currentWorkspace?.id
      );
      localStorage.setItem("replay_scenario_draft", JSON.stringify(response.draft_scenario));
      navigate("/live-simulations/scenarios/create");
    } catch (error) {
      console.error("Failed to generate replay scenario:", error);
    } finally {
      setReplayLoading(false);
    }
  };

  const handleMarkIncorrect = (m) => {
    setAnnotateModal({
      metricId: m.metric_id,
      metricName: m.metric_name,
      resultType: m.result_type || "boolean",
      resultTypeConfig: m.result_type_config,
      measuredValue: m.result_data?.value ?? (m.result_data?.metric_result || m.metric_result)?.[0]?.metric_value,
    });
  };

  return (
    <div className="px-4 pb-8">
      {/* Back button */}
      <button
        onClick={() => navigate("/observability/conversations")}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 mt-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Conversations
      </button>

      {/* Title row */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Call #{call.id}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {call.agent?.name} ·{" "}
            {new Date(call.created_at).toLocaleString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Debug button — super admin only */}
          {user?.roles?.includes("super_admin") && (
            <button
              onClick={() => setDebugOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground border border-border rounded-lg px-3 py-1.5 hover:bg-muted/50 transition-colors"
              title="Debug this call (super admin)"
            >
              <Bug className="h-3.5 w-3.5" />
              Debug
            </button>
          )}

          {/* Create Replay Scenario */}
          {canCreateReplayScenario && (
            <button
              onClick={handleCreateReplayScenario}
              disabled={replayLoading}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground border border-border rounded-lg px-3 py-1.5 hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <Play className="h-3.5 w-3.5" />
              {replayLoading ? "Generating..." : "Create Replay Scenario"}
            </button>
          )}

          {/* Rerun dropdown */}
          {canRerunMetrics && (
            <div className="relative">
              <button
                onClick={() => setRerunDropdownOpen((v) => !v)}
                disabled={rerunning}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50"
              >
                <RotateCw className={`h-3.5 w-3.5 ${rerunning ? "animate-spin" : ""}`} />
                <span>Rerun</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {rerunDropdownOpen && rerunMode === "menu" && (
                <div className="absolute right-0 mt-1 w-52 bg-card border border-border rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => handleRerun("all")}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 rounded-t-lg"
                  >
                    All Metrics
                  </button>
                  <button
                    onClick={() => setRerunMode("select")}
                    disabled={rerunableMetrics.length === 0}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 rounded-b-lg border-t border-border disabled:opacity-50"
                  >
                    Specific Metrics…
                  </button>
                </div>
              )}
              {rerunDropdownOpen && rerunMode === "select" && (
                <div className="absolute right-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg z-20">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="text-xs font-medium text-muted-foreground">
                      Select metrics to rerun
                    </span>
                    <button
                      onClick={() =>
                        setSelectedMetricIds(
                          selectedMetricIds.length === rerunableMetrics.length
                            ? []
                            : rerunableMetrics.map((m) => m.id)
                        )
                      }
                      className="text-xs text-primary hover:underline"
                    >
                      {selectedMetricIds.length === rerunableMetrics.length ? "Clear" : "All"}
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {rerunableMetrics.map((m) => (
                      <label
                        key={m.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMetricIds.includes(m.id)}
                          onChange={() => toggleMetricSelection(m.id)}
                          className="h-3.5 w-3.5 rounded border-border"
                        />
                        <span className="truncate">{m.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
                    <button
                      onClick={() => setRerunMode("menu")}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleRerunSelected}
                      disabled={selectedMetricIds.length === 0}
                      className="ml-auto px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
                    >
                      Rerun {selectedMetricIds.length || ""}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Prev/Next navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => adjacentIds.prev && navigate(`/observability/conversations/${adjacentIds.prev}`)}
              disabled={!adjacentIds.prev}
              className={`p-1 rounded border border-border ${adjacentIds.prev ? "hover:bg-muted text-muted-foreground" : "text-muted-foreground/50 cursor-not-allowed"}`}
              title="Previous call"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => adjacentIds.next && navigate(`/observability/conversations/${adjacentIds.next}`)}
              disabled={!adjacentIds.next}
              className={`p-1 rounded border border-border ${adjacentIds.next ? "hover:bg-muted text-muted-foreground" : "text-muted-foreground/50 cursor-not-allowed"}`}
              title="Next call"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ height: "calc(100vh - 180px)" }}>
        {/* ── Left: Analysis (scrollable) ── */}
        <div className="space-y-4 overflow-y-auto pr-2">
          {!isTextConversation && (
            <StandardMetricsPanel
              standardMetrics={call.standard_metrics || []}
              analysisFailed={analysisFailed}
              onTimestampClick={handleTimestampClick}
              onMarkIncorrect={handleMarkIncorrect}
            />
          )}

          <SystemLlmMetricsSection
            metrics={call.system_llm_metrics || []}
            onSeek={(t) => setSeekTo(t)}
            onMarkIncorrect={handleMarkIncorrect}
            onRerunMetric={handleRerunSingleMetric}
          />

          <CustomLlmMetricsSection
            metrics={call.llm_metrics || []}
            onSeek={(t) => setSeekTo(t)}
            onMarkIncorrect={handleMarkIncorrect}
            onRerunMetric={handleRerunSingleMetric}
          />

          <CallInsightsPanel insights={call.llm_insights} />

          {/* Metadata */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Metadata
            </h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block">Call ID</span>
                <span className="font-medium">{call.id}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Call Identifier</span>
                <span className="font-medium font-mono text-xs">{call.call_identifier || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Direction</span>
                <span className="font-medium capitalize">
                  {call.call_direction || "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Call Success</span>
                {call.call_success === true ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Pass</span>
                ) : call.call_success === false ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">Fail</span>
                ) : call.analysis_status === "error" || call.analysis_status === "failed" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">Error</span>
                ) : call.analysis_status === "completed" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">Evaluating</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Pending</span>
                )}
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Duration</span>
                <span className="font-medium">
                  {call.call_duration
                    ? `${Math.floor(call.call_duration / 60)}m ${call.call_duration % 60}s`
                    : call.generated_transcript?.audio_duration
                      ? `${Math.round(call.generated_transcript.audio_duration)}s`
                      : "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Customer Number</span>
                <span className="font-medium">{call.customer_number || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Call Time</span>
                <span className="font-medium">
                  {call.call_timestamp
                    ? new Date(call.call_timestamp).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: "UTC",
                      })
                    : new Date(call.created_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: "UTC",
                      })}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Source</span>
                <span className="font-medium capitalize">
                  {call.source?.replace("_", " ") || "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Agent</span>
                <span className="font-medium">{call.agent?.name || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Tag</span>
                <span className="font-medium">{call.tag || "—"}</span>
              </div>
              {call.metadata && Object.entries(call.metadata).map(([k, v]) => (
                <div key={k}>
                  <span className="text-xs text-muted-foreground block uppercase tracking-wide">
                    {k}
                  </span>
                  <span className="font-medium break-words">
                    {v !== null && typeof v === "object"
                      ? JSON.stringify(v, null, 2)
                      : String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right: Waveform + Transcript (sticky) ── */}
        <div className="flex flex-col gap-4 sticky top-0 h-full min-h-0">
          {/* Waveform player (audio only — hidden for text conversations) */}
          {isTextConversation ? null : isCallFailed(call) ? (
            <div className="bg-card border border-red-100 rounded-xl p-4 text-sm text-foreground shrink-0">
              <div className="font-medium text-red-700 mb-1">Call failed — no recording available</div>
              {call.ended_reason && (
                <div className="text-muted-foreground">Reason: <span className="font-mono text-xs">{call.ended_reason}</span></div>
              )}
              {call.error_message && (
                <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap break-words">
                  {call.error_message}
                </div>
              )}
            </div>
          ) : isRecordingPending(call) ? (
            <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 text-sm text-muted-foreground shrink-0">
              <svg
                className="animate-spin h-4 w-4 text-indigo-500 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Recording is being uploaded… it will appear here automatically.
            </div>
          ) : call.call_recording_url ? (
            <div className="bg-card border border-border rounded-xl p-4 shrink-0">
              <AudioWaveformPlayer
                audioUrl={call.call_recording_url}
                fileName={`call_${call.id}`}
                channelLabel={
                  channelLabel || { ai_channel: 0, user_channel: 1 }
                }
                onTimeUpdate={setCurrentTime}
                seekTo={seekTo}
              />
            </div>
          ) : null}

          {/* Transcript */}
          <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col min-h-0 flex-1">
            <div className="px-4 py-3 border-b border-border shrink-0">
              <h3 className="text-sm font-semibold text-foreground">
                Transcript
              </h3>
            </div>
            <div className="overflow-y-auto flex-1">
              <TranscriptPanel
                transcript={call.generated_transcript}
                callTranscript={call.call_transcript}
                transcriptionStatus={call.transcription_status}
                currentTime={currentTime}
                onSeek={(sec) => setSeekTo({ seconds: sec, id: Date.now() })}
                aiChannel={call.ai_channel ?? 0}
              />
            </div>
          </div>
        </div>
      {/* Annotate Metric Modal */}
      {annotateModal && (
        <AnnotateMetricModal
          visible={true}
          onClose={() => setAnnotateModal(null)}
          onSubmit={async ({ expected_value, explanation }) => {
            await metricAnnotationsAPI.create(currentWorkspace.id, {
              metric_id: annotateModal.metricId,
              annotatable_type: "CallObservation",
              annotatable_id: call.call_observation_id || call.id,
              agent_id: call.agent_id,
              measured_value: { value: annotateModal.measuredValue },
              expected_value,
              explanation,
            });
            setCall((prev) => ({
              ...prev,
              llm_metrics: (prev.llm_metrics || []).map((m) =>
                m.metric_id === annotateModal.metricId
                  ? { ...m, status: "under_investigation" }
                  : m
              ),
            }));
          }}
          metricName={annotateModal.metricName}
          resultType={annotateModal.resultType}
          resultTypeConfig={annotateModal.resultTypeConfig}
          measuredValue={annotateModal.measuredValue}
        />
      )}

      {/* Debug drawer — super admin only */}
      {debugOpen && call && (
        <DebugDrawer
          callId={call.id}
          targetLabel={`Call #${call.id}`}
          onClose={() => setDebugOpen(false)}
        />
      )}
      </div>
    </div>
  );
};

export default ConversationDetail;
